import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Upload, FileText, Link as LinkIcon, LayoutTemplate, Copy, Wand2, ArrowLeft, Loader2, CheckCircle2, AlertTriangle, Save, Play, Sparkles } from "lucide-react";

const CLASSIFICATIONS = [
  ["REQUIRED_BEFORE_HIRE","Required before hire","bg-red-50 border-red-300 text-red-800"],
  ["PREFERRED","Preferred","bg-amber-50 border-amber-300 text-amber-800"],
  ["TRAINABLE_AFTER_HIRE","Trainable after hire","bg-blue-50 border-blue-300 text-blue-800"],
  ["NOT_REQUIRED","Not required","bg-slate-100 border-slate-200 text-slate-600"],
];

export default function SmartJobBuilder() {
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prevJobs, setPrevJobs] = useState([]);

  useEffect(() => {
    api.get("/job-builder/templates").then(r=>setTemplates(r.data.templates)).catch(e=>console.error("templates load", e));
    api.get("/employer/job-orders").then(r=>setPrevJobs(r.data)).catch(e=>console.error("prev jobs load", e));
    if (editId) { api.get(`/job-orders/${editId}`).then(r=>{setJob(r.data); validate(editId);}).catch(e=>{console.error(e); toast.error("Load failed");}); }
  }, [editId]);

  const validate = async (jid) => {
    try { const { data } = await api.get(`/job-orders/${jid}/validate`); setValidation(data); }
    catch (e) { console.error("validate failed", e); }
  };

  const start = async (endpoint, payload, isFile=false) => {
    setBusy(true);
    try {
      const config = isFile ? {headers:{"Content-Type":"multipart/form-data"}} : {};
      const { data } = await api.post(endpoint, payload, config);
      setJob(data); nav(`/employer/job-builder?edit=${data.id}`, {replace:true}); validate(data.id);
      toast.success("Draft created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const savePatch = async (patch) => {
    if (!job) return;
    try {
      const { data } = await api.patch(`/job-orders/${job.id}`, patch);
      setJob(data); validate(job.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const classify = async (idx, cls) => {
    try {
      const { data } = await api.patch(`/job-orders/${job.id}/classify-requirement`, {index: idx, classification: cls});
      setJob(data); validate(job.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const confirmField = async (fieldName) => {
    try {
      await api.post(`/job-orders/${job.id}/confirm-fields`, {fields: [fieldName]});
      const { data } = await api.get(`/job-orders/${job.id}`);
      setJob(data); validate(job.id);
      toast.success("Field confirmed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const generatePost = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/job-orders/${job.id}/generate-post`);
      setJob(prev => ({...prev, generated_post: data.generated_post}));
      toast.success(`Generated ${data.sections_included.length} sections`);
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Post generation failed"); }
    finally { setBusy(false); }
  };

  const lifecycle = async (action) => {
    try { const { data } = await api.post(`/job-orders/${job.id}/lifecycle`, {action}); setJob(data); validate(job.id); toast.success(`Job ${data.status}`); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Blocked"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-slate-100 px-6 py-3 flex items-center gap-3">
        <button onClick={()=>nav("/employer/dashboard")} className="text-xs inline-flex items-center gap-1"><ArrowLeft size={12}/> Back</button>
        <div className="font-display font-bold text-sm">ConnectMyHub Smart Job Builder™</div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-8">
        {!job ? (
          <StartScreen templates={templates} prevJobs={prevJobs} onStart={start} busy={busy}/>
        ) : (
          <JobEditor job={job} validation={validation} onSavePatch={savePatch} onClassify={classify}
                     onConfirmField={confirmField} onGeneratePost={generatePost} onLifecycle={lifecycle} busy={busy}/>
        )}
      </main>
      <Toaster position="top-right" richColors/>
    </div>
  );
}

function StartScreen({templates, prevJobs, onStart, busy}) {
  const [pasteText, setPasteText] = useState("");
  const [url, setUrl] = useState("");
  const handleUpload = e => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); onStart("/job-builder/from-file", fd, true); e.target.value=""; };
  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mb-2">How would you like to create this job?</h1>
      <p className="text-sm text-slate-500 mb-8">Import an existing description, start from a template, or build guided. Anything ConnectMyHub extracts stays clearly marked <b>AI extracted · unconfirmed</b> until you confirm it.</p>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="p-5 border-2 border-dashed border-slate-300 rounded-xl hover:border-sky-400 hover:bg-sky-50/40 cursor-pointer" data-testid="src-upload">
          <div className="flex items-center gap-2 mb-2"><Upload size={16} className="text-sky-600"/><span className="font-semibold text-slate-900">Upload job description</span></div>
          <p className="text-xs text-slate-500 mb-2">PDF / DOCX / TXT · max 5 MB. ConnectMyHub extracts, you confirm.</p>
          <input type="file" accept=".pdf,.docx,.txt" onChange={handleUpload} className="text-xs"/>
        </label>
        <div className="p-5 border-2 border-slate-200 rounded-xl">
          <div className="flex items-center gap-2 mb-2"><FileText size={16} className="text-sky-600"/><span className="font-semibold text-slate-900">Paste job description</span></div>
          <textarea data-testid="src-paste" rows={4} placeholder="Paste any job description text…" value={pasteText} onChange={e=>setPasteText(e.target.value)} className="w-full px-3 py-2 rounded border border-slate-300 text-xs resize-none mb-2"/>
          <button data-testid="src-paste-btn" disabled={busy || pasteText.length<50} onClick={()=>onStart("/job-builder/from-text",{text:pasteText})} className="h-9 px-3 rounded bg-slate-900 text-white text-xs font-semibold disabled:opacity-40 inline-flex items-center gap-1.5">{busy?<Loader2 size={11} className="animate-spin"/>:<Sparkles size={11}/>} Extract</button>
        </div>
        <div className="p-5 border-2 border-slate-200 rounded-xl">
          <div className="flex items-center gap-2 mb-2"><LinkIcon size={16} className="text-sky-600"/><span className="font-semibold text-slate-900">Import from URL</span></div>
          <p className="text-xs text-slate-500 mb-2">Best-effort scrape of public job pages. Some sites may block automated fetches.</p>
          <div className="flex gap-2">
            <input data-testid="src-url" value={url} onChange={e=>setUrl(e.target.value)} placeholder="https://…" className="flex-1 h-9 px-3 rounded border border-slate-300 text-xs"/>
            <button data-testid="src-url-btn" disabled={busy || !url.startsWith("http")} onClick={()=>onStart("/job-builder/from-url",{url})} className="h-9 px-3 rounded bg-slate-900 text-white text-xs font-semibold disabled:opacity-40">Import</button>
          </div>
        </div>
        <div className="p-5 border-2 border-slate-200 rounded-xl">
          <div className="flex items-center gap-2 mb-3"><Wand2 size={16} className="text-sky-600"/><span className="font-semibold text-slate-900">Build with ConnectMyHub</span></div>
          <p className="text-xs text-slate-500 mb-2">Start with a blank draft and let ConnectMyHub guide you.</p>
          <button data-testid="src-guided" onClick={()=>onStart("/job-builder/from-template",{template_code:"CSR"})} className="h-9 px-3 rounded border border-slate-300 text-xs font-semibold">Start blank draft</button>
        </div>
      </div>

      <div className="mt-8 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3"><LayoutTemplate size={16} className="text-sky-600"/><h3 className="font-semibold text-slate-900">Start from a role template</h3></div>
        <div className="grid md:grid-cols-3 gap-3">{templates.map(t => (
          <button key={t.code} data-testid={`tmpl-${t.code}`} disabled={busy} onClick={()=>onStart("/job-builder/from-template",{template_code:t.code})} className="text-left p-3 border border-slate-200 rounded hover:border-sky-400">
            <div className="text-sm font-semibold text-slate-900">{t.name}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{t.role_family}</div>
            <div className="text-[10px] text-slate-400 mt-1">{t.requirements.length} starter requirements</div>
          </button>
        ))}</div>
      </div>

      {prevJobs.length>0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3"><Copy size={16} className="text-sky-600"/><h3 className="font-semibold text-slate-900">Duplicate a previous job</h3></div>
          <div className="grid md:grid-cols-2 gap-2">{prevJobs.slice(0,6).map(j => (
            <button key={j.id} data-testid={`dup-${j.id}`} disabled={busy} onClick={()=>onStart("/job-builder/duplicate",{source_job_id:j.id})} className="text-left p-3 border border-slate-200 rounded hover:border-sky-400 flex justify-between items-center">
              <span><span className="text-sm font-semibold text-slate-900">{j.role_title}</span><span className="text-[11px] text-slate-500 ml-2">({j.status})</span></span>
              <Copy size={13} className="text-slate-400"/>
            </button>
          ))}</div>
        </div>
      )}
    </div>
  );
}

function JobEditor({job, validation, onSavePatch, onClassify, onConfirmField, onGeneratePost, onLifecycle, busy}) {
  const [f, setF] = useState(job);
  useEffect(() => setF(job), [job.id, job.updated_at]);
  const cs = f.compensation_structure || {};
  const unconfirmed = new Set(f.ai_extracted_unconfirmed_fields || []);
  const patch = (obj) => onSavePatch(obj);
  const setComp = (k, v) => onSavePatch({compensation_structure: {...cs, [k]: v}});

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">{f.source_type || "DRAFT"} · Job {f.status}</div>
          <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="jobed-title">{f.role_title || "Untitled"}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {f.status==="Draft" && <button data-testid="lc-submit" onClick={()=>onLifecycle("SUBMIT_FOR_REVIEW")} className="h-9 px-3 rounded bg-sky-600 text-white text-xs font-semibold">Submit for review</button>}
          {(f.status==="Draft" || f.status==="In Review") && (
            <div className="inline-flex flex-col items-end gap-1">
              <button data-testid="lc-open" onClick={()=>onLifecycle("APPROVE_AND_OPEN")}
                       disabled={!validation?.ready_to_open || !f.generated_post_confirmed}
                       className="h-9 px-3 rounded bg-emerald-600 text-white text-xs font-semibold disabled:opacity-40 inline-flex items-center gap-1">
                <CheckCircle2 size={12}/> Approve & open
              </button>
              {!f.generated_post_confirmed && (
                <span data-testid="lc-open-hint" className="text-[10px] text-amber-700">Confirm the public job posting before opening this job.</span>
              )}
            </div>
          )}
          {f.status==="Open" && <button data-testid="lc-pause" onClick={()=>onLifecycle("PAUSE")} className="h-9 px-3 rounded border border-amber-300 bg-amber-50 text-amber-800 text-xs font-semibold">Pause</button>}
          {f.status==="Paused" && <button data-testid="lc-resume" onClick={()=>onLifecycle("RESUME")} className="h-9 px-3 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 text-xs font-semibold">Resume</button>}
        </div>
      </div>

      {f.status==="Open" && f.generated_post_confirmed && (
        <div data-testid="published-banner" className="p-3 rounded-lg border border-emerald-300 bg-emerald-50 flex items-center justify-between flex-wrap gap-2">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-900">
            <CheckCircle2 size={14}/> Published to ConnectMyHub Marketplace
          </div>
          <a href={`/jobs/${f.id}`} target="_blank" rel="noreferrer" data-testid="view-public-listing"
              className="text-xs font-semibold text-emerald-800 hover:text-emerald-900 underline">View public listing →</a>
        </div>
      )}

      {validation && validation.issues.length>0 && (
        <div className="p-4 rounded-lg border border-amber-300 bg-amber-50">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle size={14} className="text-amber-700"/><span className="text-sm font-semibold text-amber-900">{validation.ready_to_open ? "Warnings" : "Fix before opening"}</span></div>
          <ul className="text-xs space-y-1">{validation.issues.map((it,i)=><li key={i} className={it.level==="error"?"text-red-800":"text-amber-800"} data-testid={`issue-${it.field}`}>· {it.message}</li>)}</ul>
        </div>
      )}

      <Section title="Role details">
        <Fld k="role_title" label="Role title" f={f} setF={setF} onBlur={()=>patch({role_title:f.role_title})} unconfirmed={unconfirmed.has("role_title")} onConfirm={()=>onConfirmField("role_title")}/>
        <Fld k="role_family" label="Role family" f={f} setF={setF} onBlur={()=>patch({role_family:f.role_family})} unconfirmed={unconfirmed.has("role_family")} onConfirm={()=>onConfirmField("role_family")}/>
        <Fld k="department" label="Department" f={f} setF={setF} onBlur={()=>patch({department:f.department})}/>
        <Fld k="responsibilities" label="Responsibilities" textarea f={f} setF={setF} onBlur={()=>patch({responsibilities:f.responsibilities})} unconfirmed={unconfirmed.has("responsibilities")} onConfirm={()=>onConfirmField("responsibilities")} className="md:col-span-2"/>
        <Fld k="day_to_day_duties" label="Day-to-day duties" textarea f={f} setF={setF} onBlur={()=>patch({day_to_day_duties:f.day_to_day_duties})} className="md:col-span-2"/>
        <Fld k="expected_outcomes" label="Expected outcomes" textarea f={f} setF={setF} onBlur={()=>patch({expected_outcomes:f.expected_outcomes})} className="md:col-span-2"/>
      </Section>

      <Section title="Requirements & Classification">
        <div className="md:col-span-2 space-y-2">
          {(f.requirements_v2||[]).map((r, idx) => (
            <div key={idx} className="border border-slate-200 rounded p-3 flex items-center gap-3 flex-wrap" data-testid={`req-${idx}`}>
              <div className="flex-1"><div className="text-sm font-medium text-slate-900">{r.label}</div><div className="text-[10px] text-slate-500 uppercase tracking-widest">{r.category}{r.ai_extracted?" · AI extracted":""}</div></div>
              <div className="flex gap-1 flex-wrap">
                {CLASSIFICATIONS.map(([c, lbl, tone]) => (
                  <button key={c} data-testid={`req-${idx}-${c}`} onClick={()=>onClassify(idx,c)} className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${r.classification===c?tone:"border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{lbl}</button>
                ))}
              </div>
            </div>
          ))}
          {(f.requirements_v2||[]).length===0 && <div className="text-xs text-slate-500 italic">No requirements captured yet.</div>}
        </div>
      </Section>

      <Section title="Compensation">
        <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Min</label><input type="number" data-testid="comp-min" value={cs.min||""} onChange={e=>setComp("min",parseFloat(e.target.value)||null)} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/></div>
        <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Max</label><input type="number" data-testid="comp-max" value={cs.max||""} onChange={e=>setComp("max",parseFloat(e.target.value)||null)} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/></div>
        <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Currency</label><input data-testid="comp-currency" value={cs.currency||"USD"} onChange={e=>setComp("currency",e.target.value)} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/></div>
        <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Period</label><select data-testid="comp-period" value={cs.period||"Monthly"} onChange={e=>setComp("period",e.target.value)} className="w-full h-10 px-3 rounded border border-slate-300 text-sm">{["Hourly","Monthly","Annual"].map(x=><option key={x}>{x}</option>)}</select></div>
        <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Commission structure</label><input data-testid="comp-commission" value={cs.commission_structure||""} onChange={e=>setComp("commission_structure",e.target.value)} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/></div>
        <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Bonus structure</label><input data-testid="comp-bonus" value={cs.bonus_structure||""} onChange={e=>setComp("bonus_structure",e.target.value)} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/></div>
        <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">OTE</label><input type="number" data-testid="comp-ote" value={cs.ote||""} onChange={e=>setComp("ote",parseFloat(e.target.value)||null)} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/></div>
        <div className="flex items-end gap-2"><label className="text-xs text-slate-700 inline-flex items-center gap-2"><input type="checkbox" data-testid="comp-display" checked={cs.display_publicly!==false} onChange={e=>setComp("display_publicly",e.target.checked)}/> Display publicly on posting</label></div>
      </Section>

      <Section title="Schedule & Location">
        <Fld k="schedule" label="Schedule / hours" f={f} setF={setF} onBlur={()=>patch({schedule:f.schedule})} unconfirmed={unconfirmed.has("schedule")} onConfirm={()=>onConfirmField("schedule")}/>
        <Fld k="hours_per_week" label="Hours / week" type="number" f={f} setF={setF} onBlur={()=>patch({hours_per_week:parseInt(f.hours_per_week)||0})}/>
        <Fld k="location_type" label="Location type" f={f} setF={setF} onBlur={()=>patch({location_type:f.location_type})} unconfirmed={unconfirmed.has("location_type")} onConfirm={()=>onConfirmField("location_type")}/>
        <Fld k="required_country" label="Required country" f={f} setF={setF} onBlur={()=>patch({required_country:f.required_country})}/>
        <Fld k="target_start" label="Target start date" f={f} setF={setF} onBlur={()=>patch({target_start:f.target_start})} unconfirmed={unconfirmed.has("target_start")} onConfirm={()=>onConfirmField("target_start")}/>
        <Fld k="num_openings" label="Number of openings" type="number" f={f} setF={setF} onBlur={()=>patch({num_openings:parseInt(f.num_openings)||1})} unconfirmed={unconfirmed.has("num_openings")} onConfirm={()=>onConfirmField("num_openings")}/>
      </Section>

      <Section title="Candidate-facing job posting">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-3">
            <button data-testid="gen-post-btn" onClick={onGeneratePost} disabled={busy || (f.ai_extracted_unconfirmed_fields||[]).length>0} className="h-10 px-4 rounded bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40">{busy?<Loader2 size={13} className="animate-spin"/>:<Sparkles size={13}/>} Generate posting</button>
            {(f.ai_extracted_unconfirmed_fields||[]).length>0 && <span className="text-xs text-amber-700">Confirm all AI-extracted fields first.</span>}
          </div>
          {f.generated_post && (
            <div className="space-y-3">{Object.entries(f.generated_post).map(([k, v]) => (
              <div key={k} data-testid={`post-${k}`}>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-sky-700 mb-1">{k.replace(/_/g," ")}</div>
                <textarea rows={typeof v==="string" ? Math.max(2, v.split(/\n/).length) : 3} value={typeof v==="string" ? v : JSON.stringify(v, null, 2)} onChange={e=>setF(prev=>({...prev, generated_post:{...prev.generated_post,[k]:e.target.value}}))} className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none"/>
              </div>
            ))}
              <button data-testid="post-save" onClick={()=>onSavePatch({generated_post: f.generated_post, generated_post_confirmed: true})} className="h-9 px-4 rounded bg-emerald-600 text-white text-xs font-semibold inline-flex items-center gap-1.5"><Save size={11}/> Save & confirm posting</button>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}

const Section = ({title, children}) => (
  <section className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
    <h3 className="text-sm font-semibold text-slate-900 mb-3">{title}</h3>
    <div className="grid md:grid-cols-2 gap-3">{children}</div>
  </section>
);
const Fld = ({k, label, f, setF, type="text", textarea, onBlur, className="", unconfirmed, onConfirm}) => (
  <div className={className}>
    <div className="flex items-center justify-between mb-1.5">
      <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      {unconfirmed && <button data-testid={`confirm-${k}`} onClick={onConfirm} className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100">AI · confirm</button>}
    </div>
    {textarea ?
      <textarea rows={3} data-testid={`fld-${k}`} value={f[k]||""} onChange={e=>setF(prev=>({...prev,[k]:e.target.value}))} onBlur={onBlur} className={`w-full px-3 py-2 rounded border text-sm resize-none ${unconfirmed?"border-amber-300 bg-amber-50/40":"border-slate-300"}`}/> :
      <input type={type} data-testid={`fld-${k}`} value={f[k]||""} onChange={e=>setF(prev=>({...prev,[k]:e.target.value}))} onBlur={onBlur} className={`w-full h-10 px-3 rounded border text-sm ${unconfirmed?"border-amber-300 bg-amber-50/40":"border-slate-300"}`}/>}
  </div>
);
